import { ChakraProvider } from '@chakra-ui/react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { type ReactNode, useMemo } from 'react';
import type { ToastContainerProps } from 'react-toastify';

import { ROCKET_THEME } from '../theme';
import { ToastProvider } from '../toast/ToastProvider';

export interface RocketProviderProps {
  children: ReactNode;
  /** Custom theme system. Defaults to ROCKET_THEME. */
  theme?: Parameters<typeof ChakraProvider>[0]['value'];
  /** Toast configuration overrides. Pass `false` to disable toasts. */
  toast?: Partial<ToastContainerProps> | false;
  /**
   * CSP nonce for Emotion runtime styles.
   * When provided, a custom Emotion cache is created with the nonce so that
   * every injected `<style>` tag carries `nonce="<value>"`.
   */
  nonce?: string;
}

export function RocketProvider({
  children,
  theme = ROCKET_THEME,
  toast,
  nonce,
}: RocketProviderProps) {
  const emotionCache = useMemo(
    () => (nonce ? createCache({ key: 'rocket', nonce }) : undefined),
    [nonce]
  );

  const inner = (
    <ChakraProvider value={theme}>
      {toast === false ? children : <ToastProvider {...(toast || {})}>{children}</ToastProvider>}
    </ChakraProvider>
  );

  if (emotionCache) {
    return <CacheProvider value={emotionCache}>{inner}</CacheProvider>;
  }

  return inner;
}
